import React from "react";
import { InputSwitch } from "primereact/inputswitch";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "src/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import { formatCurrency, formatDate } from "../../utils/billing-utils";
import type {
  CommissionInvoice,
  Referral,
} from "../types/commissions.types";

interface CommissionInvoiceDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: CommissionInvoice | null;
  referrals: Referral[];
  onToggleIncludeInTotal?: (invoice: CommissionInvoice, includeInTotal: boolean) => void;
}

const CommissionInvoiceDrawer: React.FC<CommissionInvoiceDrawerProps> = ({
  open,
  onOpenChange,
  invoice,
  referrals,
  onToggleIncludeInTotal,
}) => {
  if (!invoice) return null;

  const referralById = referrals.reduce<Record<string, Referral>>(
    (acc, r) => {
      acc[r.id] = r;
      return acc;
    },
    {}
  );

  const assignments = invoice.referralAssignments ?? [];
  const lines = invoice.lines ?? [];

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetContent className="!w-[60vw] sm:!w-[75vw] !max-w-none overflow-y-auto bg-white">
        <SheetHeader>
          <SheetTitle>
            Invoice —
            {invoice.invoiceNumber}
          </SheetTitle>
        </SheetHeader>
        <div className="grid gap-6 py-4">
          <section className="grid gap-3">
            <div className="grid grid-cols-[minmax(0,8rem)_1fr] gap-x-4 gap-y-1 items-baseline">
              <span className="text-sm font-medium text-gray-600">Invoice #</span>
              <span className="text-sm text-gray-900">{invoice.invoiceNumber}</span>
              <span className="text-sm font-medium text-gray-600">Company</span>
              <span className="text-sm text-gray-900">{invoice.companyName}</span>
              <span className="text-sm font-medium text-gray-600">Amount</span>
              <span className="text-sm text-gray-900 font-medium">
                {formatCurrency(invoice.amount)}
              </span>
              <span className="text-sm font-medium text-gray-600">Invoice date</span>
              <span className="text-sm text-gray-900">
                {invoice.createdAt ? formatDate(invoice.createdAt) : "—"}
              </span>
              <span className="text-sm font-medium text-gray-600">Paid date</span>
              <span className="text-sm text-gray-900">
                {invoice.paidDate ? formatDate(invoice.paidDate) : "—"}
              </span>
              <span className="text-sm font-medium text-gray-600">Status</span>
              <span className="text-sm">
                <span
                  className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                    invoice.status === "paid"
                      ? "bg-green-100 text-green-800"
                      : "bg-amber-100 text-amber-800"
                  }`}
                >
                  {invoice.status === "paid" ? "Paid" : "Unpaid"}
                </span>
              </span>
              <span className="text-sm font-medium text-gray-600">Include in total</span>
              <span className="text-sm flex items-center gap-2">
                <InputSwitch
                  checked={invoice.includeInTotal !== false}
                  onChange={(e) =>
                    onToggleIncludeInTotal?.(invoice, e.value ?? true)
                  }
                  disabled={!onToggleIncludeInTotal}
                />
              </span>
            </div>
          </section>

          {assignments.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">
                Referrals assigned
              </h3>
              <ul className="rounded-md border border-gray-200 divide-y divide-gray-200">
                {assignments.map((a) => {
                  const ref = referralById[a.referralId];
                  return (
                    <li
                      key={a.referralId}
                      className="flex items-center justify-between px-3 py-2 text-sm"
                    >
                      <span className="font-medium text-gray-900">
                        {ref?.organizationName ?? a.referralId}
                      </span>
                      <span className="text-gray-600">
                        Rate: {a.rate}% · Payout: {formatCurrency(a.payoutAmount)}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <p className="text-xs text-gray-500 mt-1">
                Total paid out:{" "}
                {formatCurrency(
                  assignments.reduce((sum, a) => sum + a.payoutAmount, 0)
                )}
              </p>
            </section>
          )}

          {assignments.length === 0 && invoice.referralId && (
            <section>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">
                Referrals assigned
              </h3>
              <p className="text-sm text-gray-600">
                No referral assignments with rate/payout on this invoice.
              </p>
            </section>
          )}

          <section>
            <h3 className="text-sm font-semibold text-gray-800 mb-2">
              Line items
            </h3>
            {lines.length > 0 ? (
              <div className="rounded-md border border-gray-200 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50 border-b border-gray-200">
                      <TableHead className="font-medium text-gray-700">Name</TableHead>
                      <TableHead className="font-medium text-gray-700">SKU</TableHead>
                      <TableHead className="font-medium text-gray-700 text-right">
                        Amount
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lines.map((line) => (
                      <TableRow
                        key={line.id}
                        className="border-b border-gray-100 last:border-0"
                      >
                        <TableCell className="text-gray-900">{line.name}</TableCell>
                        <TableCell className="text-gray-900">{line.sku}</TableCell>
                        <TableCell className="text-right tabular-nums text-gray-900">
                          {formatCurrency(line.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No line items.</p>
            )}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CommissionInvoiceDrawer;
